import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { LSC_PROGRAMS, LSC_PROGRAM_GROUPS } from "@/lib/institution";

// Searchable (type-to-filter) dropdown for the LSC programs-of-study list,
// grouped by Area of Study. Small, wrapping items so full names are visible.
// Preserves any pre-existing value that isn't in the current catalog.
export function ProgramCombobox({ value, onChange, testid, size = "default", placeholder = "Select program" }) {
  const [open, setOpen] = useState(false);
  const isLegacy = value && !LSC_PROGRAMS.includes(value);
  const h = size === "sm" ? "h-8" : "h-9";

  const pick = (v) => { onChange(v); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testid}
          className={cn("mt-1 w-full justify-between font-normal", h, !value && "text-muted-foreground")}
        >
          <span className="truncate text-xs">{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search programs…" className="text-xs" data-testid={testid ? `${testid}-search` : undefined} />
          <CommandList className="max-h-72">
            <CommandEmpty className="py-4 text-center text-xs text-slate-500">No program found.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="Not specified" onSelect={() => pick("")}>
                <Check className={cn("mr-2 h-3.5 w-3.5 shrink-0", !value ? "opacity-100" : "opacity-0")} />
                <span className="text-xs leading-snug">Not specified</span>
              </CommandItem>
            </CommandGroup>
            {isLegacy && (
              <CommandGroup heading="Current value">
                <CommandItem value={value} onSelect={() => pick(value)}>
                  <Check className="mr-2 h-3.5 w-3.5 shrink-0 opacity-100" />
                  <span className="whitespace-normal text-xs leading-snug">{value}</span>
                </CommandItem>
              </CommandGroup>
            )}
            {LSC_PROGRAM_GROUPS.map((g) => (
              <CommandGroup key={g.area} heading={g.area}>
                {g.programs.map((p) => (
                  <CommandItem key={p} value={p} onSelect={() => pick(p)}>
                    <Check className={cn("mr-2 h-3.5 w-3.5 shrink-0", value === p ? "opacity-100" : "opacity-0")} />
                    <span className="whitespace-normal text-xs leading-snug">{p}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
